import { CalendarClock, ClipboardList, Eye, type LucideIcon } from "lucide-react";
import { DEFAULT_CURRENCY } from "@/constants/common";
import type { GetUserProfileResponse, UserType } from "@/domain/core";

// App Roles Names
export const APP_ROLES = {
  ADMIN_USER: "Admin.User",
  PLATFORM_USER: "Platform.User",
  PROFESSIONAL_USER: "Professional.User",
  PRACTICE_USER: "Practice.User"
} as const;

// App Roles Types
export type AppRole = (typeof APP_ROLES)[keyof typeof APP_ROLES];

// Msal Init State
export interface MsalInitState {
  isInitialized: boolean;
  error: string | null;
  isLoading: boolean;
}

// Identity provider ids. Social values must match Entra External ID
// `identityProviderType` / `domain_hint` (PascalCase).
export const IDENTITY_PROVIDERS = {
  GOOGLE: "Google",
  APPLE: "Apple",
  EMAIL: "Email"
} as const;

// Identity Provider Types
export type IdentityProvider =
  (typeof IDENTITY_PROVIDERS)[keyof typeof IDENTITY_PROVIDERS];

// PWA Install State
export interface PWAInstallState {
  canInstall: boolean;
  isInstalled: boolean;
  isInstalling: boolean;
  showPrompt: boolean;
  deferredPrompt: BeforeInstallPromptEvent | null;
}

// PWA State
export interface PWAState {
  isInstalled: boolean;
  isUpdateAvailable: boolean;
  isOnline: boolean;
  isLoading: boolean;
  needRefresh: boolean;
  offlineReady: boolean;
}

// Module Route
export interface ModuleRoute {
  path: string;
  label: string;
  icon?: string;
  roles?: AppRole[];
  children?: ModuleRoute[];
}

/**
 * Error body surfaced by the base query after unwrapping the unified envelope.
 * Mirrors `Tempii.Shared.ApiError` (`{ code, message, type, details }`), plus
 * envelope meta fields the unwrap step flattens onto the RTK error.
 */
export interface ApiErrorDetail {
  code?: string;
  message?: string;
  type?: string;
  details?: readonly ApiErrorDetailItem[];
  correlationId?: string;
  timestamp?: string;
  version?: string;
  status?: number;
}

export interface ApiErrorDetailItem {
  code: string;
  message: string;
}

// User Info
export interface UserInfo {
  userId: string;
  name: string;
  email: string;
  firstName?: string;
  middleName?: string;
  lastName?: string;
}

// User Details
export interface UserDetails {
  // Basic user info from token
  name: string;
  email: string;

  // Full user details from backend
  user?: GetUserProfileResponse;

  // Loading states
  isLoading: boolean;
  isError: boolean;
  error?: unknown;
  apiError?: ApiErrorDetail;

  // Specific error scenarios
  isUserInactive: boolean;
  isServerError: boolean;

  // Registration details
  isRegistrationRequired: boolean;
  registrationMessage: string;

  // Computed properties
  userId?: string;
  userType?: UserType;
  isProfessional: boolean;
  isPractice: boolean;
  isAdmin: boolean;
  isSupport: boolean;
  isStaff: boolean;

  // Refetch method for manual refresh
  refetch?: () => Promise<object>;
}

// User Details Context
export interface UserDetailsContext extends UserDetails {
  getRedirectRoute?: () => string | null;
}

// Session State
export interface SessionState {
  isActive: boolean;
  userId?: string;
  sessionId: string;
  createdAt: Date;
  lastActivity: Date;
  expiresAt: Date;
}

// Session Config
export interface SessionConfig {
  sessionTimeout: number; // in minutes
  maxSessionAge: number; // in hours
  activityThreshold: number; // in minutes
}

// Navigation Item
export interface NavigationItem {
  title: string;
  url: string;
  icon: React.ComponentType<{ className?: string }>;
  children?: NavigationItem[];
  /** When true, omit from sidebar and menus. Route/page stays registered. */
  hidden?: boolean;
}

// Navigation Config
export interface NavigationConfig {
  main: NavigationItem[];
  secondary: NavigationItem[];
}

// Log Level
export enum LogLevel {
  ERROR = "error",
  WARN = "warn",
  INFO = "info",
  DEBUG = "debug"
}

// Log Context
export interface LogContext {
  component?: string;
  function?: string;
  userId?: string;
  sessionId?: string;
  [key: string]: unknown;
}

// Professional Shift Sort By — canonical keys from `GetProfessionalShiftsSortConfiguration`
// / `GetProfessionalUpcomingShiftsSortConfiguration`. Backend still accepts legacy
// `ShiftPost.*` aliases; the SPA only sends these canonical values.
export type ProfessionalShiftSortBy =
  | "ShiftDate"
  | "Title"
  | "Practice.PracticeName"
  | "AppliedAt"
  | "HourlyRate"
  | "Status";

/** Value/label pair for filter and form selects. */
export type SelectOption<T extends string = string> = {
  value: T;
  label: string;
};

export const PROFILE_CARD_MODES = ["compact", "extended"] as const;

/** Compact vs detailed layout for profile / directory cards. */
export type ProfileCardMode = (typeof PROFILE_CARD_MODES)[number];

export function isProfileCardMode(value: string): value is ProfileCardMode {
  return (PROFILE_CARD_MODES as readonly string[]).includes(value);
}

/**
 * Preview Size Types
 */
export type PreviewSize =
  | "sm"
  | "md"
  | "lg"
  | "xl"
  | "xl2"
  | "xl3"
  | "xl4"
  | "xl5"
  | "xl6"
  | "xl7"
  | "xl8"
  | "xl9"
  | "xl10"
  | "xl11"
  | "xl12";

/**
 * Preview Size Options
 */
export const PREVIEW_SIZES: Record<PreviewSize, string> = {
  sm: "w-16 h-16",
  md: "w-24 h-24",
  lg: "w-32 h-32",
  xl: "w-40 h-40",
  xl2: "w-48 h-48",
  xl3: "w-56 h-56",
  xl4: "w-64 h-64",
  xl5: "w-72 h-72",
  xl6: "w-80 h-80",
  xl7: "w-88 h-88",
  xl8: "w-96 h-96",
  xl9: "w-104 h-104",
  xl10: "w-112 h-112",
  xl11: "w-120 h-120",
  xl12: "w-128 h-128"
} as const;

/**
 * Currency Options
 */
export const CURRENCY_OPTIONS = [
  { value: DEFAULT_CURRENCY, label: "CAD ($)" }
] as const;

/**
 * Application Constants
 */
export const APP_CONSTANTS = {
  MIN_CERTIFICATE_DATE: new Date("1950-01-01"),
  MAX_CERTIFICATE_DATE: new Date(),
  MAX_EXPIRY_DATE: new Date(`${new Date().getFullYear() + 20}-01-01`)
} as const;


/**
 * Post Shift Step Meta
 */
export const POST_SHIFT_STEP_META: Array<{
  title: string;
  description: string;
  icon: LucideIcon;
}> = [
    {
      title: "Shift Details",
      description: "Define the role, requirements, and compensation",
      icon: ClipboardList
    },
    {
      title: "Shift Schedule",
      description: "Set up when and how often this shift repeats",
      icon: CalendarClock
    },
    {
      title: "Preview & Post",
      description: "Review everything before publishing",
      icon: Eye
    }
  ];